// Prices, quantities and add-on status are illustrative until the financial model is final.

export const INCLUDED = { kind: 'included' } as const;
export const NOT_INCLUDED = { kind: 'not-included' } as const;

export type PlanCell = string | typeof INCLUDED | typeof NOT_INCLUDED;

export type PlanRow = {
  label: string;
  note?: string;
  tag?: 'Add-on' | 'Bundle';
  free: PlanCell;
  payAsYouGo: PlanCell;
};

export type PlanRowGroup = {
  name?: string;
  rows: PlanRow[];
};

export type IncludedCategory = {
  title: string;
  summary: string;
  rows: PlanRow[];
};

export type FaqEntry = {
  question: string;
  answer: string;
};

function sameOnBoth(label: string, value: PlanCell): PlanRow {
  return { label, free: value, payAsYouGo: value };
}

function includedOnBoth(label: string): PlanRow {
  return sameOnBoth(label, INCLUDED);
}

function notIncluded(label: string): PlanRow {
  return sameOnBoth(label, NOT_INCLUDED);
}

function addOn(label: string, payAsYouGo: string, tag: PlanRow['tag'] = 'Add-on'): PlanRow {
  return { label, tag, free: NOT_INCLUDED, payAsYouGo };
}

export const PRICING_GROUPS: PlanRowGroup[] = [
  {
    name: 'Message inside your product',
    rows: [
      sameOnBoth('In-app messages', 'Free'),
      sameOnBoth('Messaging inbox', 'Free'),
      sameOnBoth('Slack', 'Free'),
      sameOnBoth('Push', 'Free up to 500'),
    ],
  },
  {
    name: 'Message outside your product',
    rows: [
      {
        label: 'Email',
        note: 'Needs a card and a funded balance',
        free: NOT_INCLUDED,
        payAsYouGo: '$1.50 / 1,000 sends',
      },
      { label: 'SMS', free: NOT_INCLUDED, payAsYouGo: 'Carrier rate' },
      { label: 'Webhooks', free: NOT_INCLUDED, payAsYouGo: '$0.40 / 1,000 calls' },
    ],
  },
  {
    name: 'Your audience',
    rows: [
      {
        label: 'Monthly active profiles',
        note: "People you message or update in a calendar month, counted once however often they're active",
        free: '500 per month',
        payAsYouGo: '$0.04 each a month',
      },
      {
        label: 'Dormant stored profiles',
        note: "Profiles held in your workspace that weren't active this month",
        free: '1,000 included',
        payAsYouGo: '$4.50 / 1,000 a month',
      },
    ],
  },
  {
    name: 'How you pay',
    rows: [{ label: 'Billing', free: 'Nothing to pay', payAsYouGo: 'Add funds, they draw down' }],
  },
];

export const INCLUDED_CATEGORIES: IncludedCategory[] = [
  {
    title: 'Agent',
    summary: 'Credits, routines, skills, memory, sessions',
    rows: [
      sameOnBoth('Agent credits', '100,000, one time'),
      addOn('Extra agent credits', 'Credit pack · $10 / 100K'),
      sameOnBoth('Routines: number of tasks', '1'),
      sameOnBoth('Routines: frequency', 'Weekly'),
      sameOnBoth('Custom execution skills', '3'),
      sameOnBoth('Session limits', 'Standard'),
      includedOnBoth('Persistent memory'),
      includedOnBoth('Core execution skills'),
    ],
  },
  {
    title: 'Journeys',
    summary: 'Workflows, broadcasts, cross-channel automation',
    rows: [
      includedOnBoth('Visual workflow builder'),
      sameOnBoth('Workflows, broadcasts, and sending domains', 'Unlimited'),
      includedOnBoth('Cross-channel campaign automation'),
      includedOnBoth('Send-time optimization'),
      includedOnBoth('API-triggered broadcasts'),
      includedOnBoth('LLM actions'),
      includedOnBoth('Scalable personalization'),
      includedOnBoth('Custom SMTP'),
      notIncluded('Anonymous messages'),
      addOn('Marketing bundle', 'Available', 'Bundle'),
    ],
  },
  {
    title: 'Audiences',
    summary: 'Segments, attributes, object types, ad sync',
    rows: [
      sameOnBoth('Data-driven segments', '10'),
      addOn('Additional data-driven segments', 'Available'),
      sameOnBoth('Object types', '2'),
      includedOnBoth('Custom objects'),
      sameOnBoth('People attributes', 'Unlimited'),
      includedOnBoth('Segment builder'),
      includedOnBoth('Suggested segments'),
      includedOnBoth('Nested data'),
      includedOnBoth('Ad audience sync'),
      notIncluded('Custom collections'),
    ],
  },
  {
    title: 'Data and integrations',
    summary: 'API, workspaces, reverse ETL, warehouse',
    rows: [
      sameOnBoth('Monthly API calls', 'Unlimited'),
      sameOnBoth('Workspaces', '2'),
      sameOnBoth('Integrations', 'Basic'),
      includedOnBoth('MCP server'),
      includedOnBoth('Data index'),
      includedOnBoth('Connected forms'),
      includedOnBoth('Privacy management'),
      includedOnBoth('Reverse ETL'),
      notIncluded('Data warehouse destinations'),
      notIncluded('Data replay'),
    ],
  },
  {
    title: 'Design Studio',
    summary: 'Editors, global styles, translation, templates',
    rows: [
      includedOnBoth('Drag-and-drop visual editor'),
      includedOnBoth('Code editor'),
      includedOnBoth('AI-generated global styles'),
      includedOnBoth('AI translator for messages'),
      includedOnBoth('Email content analysis'),
      includedOnBoth('Reusable components'),
      includedOnBoth('Saved templates and version control'),
    ],
  },
  {
    title: 'Reporting',
    summary: 'Dashboards, testing, conversions, history',
    rows: [
      includedOnBoth('Dashboards'),
      includedOnBoth('Campaign reporting'),
      includedOnBoth('Workspace performance dashboard'),
      includedOnBoth('Survey analysis'),
      includedOnBoth('A/B and multivariate testing'),
      includedOnBoth('Conversion tracking'),
      includedOnBoth('Data export'),
      includedOnBoth('Universal search'),
      sameOnBoth('Activity log and delivery history', '7 days'),
    ],
  },
  {
    title: 'Security and compliance',
    summary: 'SSO, residency, SOC, permissions, deliverability',
    rows: [
      includedOnBoth('SSO and two-factor authentication'),
      includedOnBoth('US and EU data centers'),
      includedOnBoth('SOC Tier II compliance'),
      includedOnBoth('Standard role-based permissions'),
      notIncluded('Custom role-based permissions'),
      notIncluded('Managed deliverability'),
      addOn('HIPAA compliance', 'Available'),
      addOn('Dedicated IP', 'Available'),
    ],
  },
  {
    title: 'Support and seats',
    summary: 'Channels, onboarding, CSM, seats',
    rows: [
      includedOnBoth('Documentation and community'),
      includedOnBoth('Email support'),
      notIncluded('Chat support'),
      notIncluded('Onboarding support'),
      sameOnBoth('Seats', 'Unlimited'),
    ],
  },
];

export const SPEND_CONTROLS: string[] = [
  'See what each meter is costing you, updated daily',
  'Estimate before you fund, using your own expected volumes',
  'Get told at 50%, 25%, and 10% of your balance',
  'Set auto reload, or leave it off and stay in control',
];

export const FAQ: FaqEntry[] = [
  {
    question: 'How does billing work?',
    answer:
      'One balance, no subscription. You add funds, usage draws them down daily, and you top up when you want to. Nothing renews on its own.',
  },
  {
    question: 'When do I actually start paying?',
    answer: 'Only once you pass a free allowance, and only on the meter you passed. Everything else stays free.',
  },
  {
    question: 'Is free really forever?',
    answer:
      "Yes. There's no trial and no countdown. If in-product messaging is all you need, you never have to pay us.",
  },
  {
    question: 'How much do I have to put in to start sending email?',
    answer: 'A small minimum, and it goes straight into your balance rather than to a subscription.',
  },
  {
    question: 'What happens when my balance runs out?',
    answer:
      'Sending stops and everything else keeps working. Your data, campaigns, and reports stay exactly where they are.',
  },
  {
    question: 'Do unused funds expire?',
    answer: "No. What you've added stays in your balance.",
  },
  {
    question: 'Can I try email before I pay?',
    answer: 'You can send test messages to your own team for free, so you can see a campaign work end to end first.',
  },
  {
    question: 'When should I talk to sales instead?',
    answer:
      'When your volumes are steady enough that a committed rate beats metered pricing, or when you need a contract, SSO, or a dedicated IP.',
  },
];
